import { schema } from '@app/bindx-schema'
import { useEntityList } from '@contember/bindx-react'
import { createContext, type ReactNode, useContext, useMemo } from 'react'

/**
 * A `Locale` row pre-loaded for `$connect`-by-id (BINDX_WORKAROUNDS: the
 * store never fetches a connected target on its own, so every field that
 * connects a locale reads it from here, never by a bare id).
 */
export type LocaleRow = {
	id: string
	code: string
	label: string
	order: number
	pathPrefix: string
	isDefault: boolean
	isPublic: boolean
}

/** All locales in display order; `null` while loading (or on error). */
export function useLocales(): LocaleRow[] | null {
	const list = useEntityList(
		schema.Locale,
		{ orderBy: [{ order: 'asc' }] },
		l => l.code().label().order().pathPrefix().isDefault().isPublic(),
	)
	const items = list.$status === 'ready' ? list.items : null
	return useMemo(() => {
		if (!items) return null
		return items.map(item => ({
			id: item.id,
			code: item.code.value ?? '',
			label: item.label.value ?? '',
			order: item.order.value ?? 0,
			pathPrefix: item.pathPrefix.value ?? '',
			isDefault: item.isDefault.value ?? false,
			isPublic: item.isPublic.value ?? false,
		}))
	}, [items])
}

const CurrentLocaleContext = createContext<LocaleRow | null>(null)

/** Set by `LocaleTabs` around the active tab; read by `SlugField` (prefix) and `LinkField` (Url filter). */
export function CurrentLocaleProvider({ locale, children }: { locale: LocaleRow; children: ReactNode }) {
	return <CurrentLocaleContext.Provider value={locale}>{children}</CurrentLocaleContext.Provider>
}

export function useCurrentLocale(): LocaleRow | null {
	return useContext(CurrentLocaleContext)
}
